import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Building2, Users as UsersIcon, AlertTriangle, Bell, Plug, MessageSquare, ShieldCheck, Navigation } from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const [shortCycle, setShortCycle] = useState([8]);
  const [maxRuntime, setMaxRuntime] = useState([180]);
  const [humidity, setHumidity] = useState([60]);
  const [offline, setOffline] = useState([60]);
  const [setpointVar, setSetpointVar] = useState([4]);
  const [sensorImb, setSensorImb] = useState([3]);
  const [highStage, setHighStage] = useState([12]);
  const [maintInterval, setMaintInterval] = useState([180]);

  const [notify, setNotify] = useState({
    email: true, sms: true, inApp: true, digest: false,
    criticalOnly: false, toDispatcher: true, toTech: true, toCustomer: false,
  });

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Settings</div>
        <h1 className="mt-1 text-3xl font-semibold text-slate-900 tracking-tight">Workspace configuration</h1>
        <p className="mt-1 text-sm text-slate-500">Manage company, users, alert thresholds, and integrations.</p>
      </div>

      <Tabs defaultValue="company" className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="company"><Building2 className="h-3.5 w-3.5 mr-1.5" />Company</TabsTrigger>
          <TabsTrigger value="users"><UsersIcon className="h-3.5 w-3.5 mr-1.5" />Users</TabsTrigger>
          <TabsTrigger value="thresholds"><AlertTriangle className="h-3.5 w-3.5 mr-1.5" />Alert Thresholds</TabsTrigger>
          <TabsTrigger value="notifications"><Bell className="h-3.5 w-3.5 mr-1.5" />Notifications</TabsTrigger>
          <TabsTrigger value="integration"><Plug className="h-3.5 w-3.5 mr-1.5" />Honeywell</TabsTrigger>
          <TabsTrigger value="templates"><MessageSquare className="h-3.5 w-3.5 mr-1.5" />Templates</TabsTrigger>
          <TabsTrigger value="plans"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" />Plans</TabsTrigger>
          <TabsTrigger value="dispatch"><Navigation className="h-3.5 w-3.5 mr-1.5" />Dispatch</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm max-w-3xl">
            <div className="text-base font-semibold text-slate-900">Company Profile</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Company Name" defaultValue="JB Heating & Air" />
              <Field label="License #" defaultValue="MA-HVAC-00123" mono />
              <Field label="Phone" defaultValue="(617) 555-0100" mono />
              <Field label="Email" defaultValue="support@jbnotify.com" mono />
              <Field label="Service Address" defaultValue="284 Commerce Way, Boston, MA 02109" className="md:col-span-2" />
            </div>
            <Button className="mt-5 bg-[#0052CC] hover:bg-[#0043A6]" onClick={() => toast.success('Company profile saved')}>Save Changes</Button>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm">
            <div className="text-base font-semibold text-slate-900">Users & Permissions</div>
            <div className="mt-4 space-y-2">
              {[
                ['Jordan Bell', 'admin@jbnotify.com', 'Owner / Admin'],
                ['Marcus Alvarez', 'marcus@jbnotify.com', 'Senior Technician'],
                ['Priya Natarajan', 'priya@jbnotify.com', 'Lead Diagnostic Tech'],
                ['Sarah Chen', 'sarah@jbnotify.com', 'Dispatcher'],
              ].map((u, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-md border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">{u[0].split(' ').map((n) => n[0]).join('')}</div>
                    <div>
                      <div className="font-medium text-slate-900 text-sm">{u[0]}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{u[1]}</div>
                    </div>
                  </div>
                  <span className="text-xs text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full">{u[2]}</span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="thresholds" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm max-w-4xl">
            <div className="text-base font-semibold text-slate-900">Alert Thresholds</div>
            <div className="mt-1 text-sm text-slate-500">Fine-tune when diagnostic alerts fire across the fleet.</div>
            <div className="mt-6 space-y-6">
              <SliderField label="Short cycling threshold" suffix="cycles / hour" value={shortCycle} onValueChange={setShortCycle} min={3} max={20} />
              <SliderField label="Max runtime before alert" suffix="minutes" value={maxRuntime} onValueChange={setMaxRuntime} min={60} max={360} step={10} />
              <SliderField label="Humidity warning threshold" suffix="% RH" value={humidity} onValueChange={setHumidity} min={40} max={75} />
              <SliderField label="Offline duration threshold" suffix="minutes" value={offline} onValueChange={setOffline} min={15} max={240} step={5} />
              <SliderField label="Setpoint variance threshold" suffix="°F" value={setpointVar} onValueChange={setSetpointVar} min={1} max={8} />
              <SliderField label="Sensor imbalance threshold" suffix="°F" value={sensorImb} onValueChange={setSensorImb} min={1} max={6} />
              <SliderField label="High-stage usage threshold" suffix="events / 24h" value={highStage} onValueChange={setHighStage} min={3} max={30} />
              <SliderField label="Maintenance interval" suffix="days" value={maintInterval} onValueChange={setMaintInterval} min={30} max={365} step={5} />
            </div>
            <Separator className="my-6" />
            <div className="flex justify-end">
              <Button onClick={() => toast.success('Thresholds saved')} className="bg-[#0052CC] hover:bg-[#0043A6]">Save Thresholds</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm max-w-3xl">
            <div className="text-base font-semibold text-slate-900">Notification Preferences</div>
            <div className="mt-4 space-y-3">
              <ToggleRow label="Email alerts" desc="Route alert emails to team inboxes" value={notify.email} onChange={(v) => setNotify({ ...notify, email: v })} />
              <ToggleRow label="SMS alerts" desc="High-priority alerts via text message" value={notify.sms} onChange={(v) => setNotify({ ...notify, sms: v })} />
              <ToggleRow label="In-app notifications" desc="Surface in the dashboard bell" value={notify.inApp} onChange={(v) => setNotify({ ...notify, inApp: v })} />
              <ToggleRow label="Daily digest" desc="Morning summary of fleet health" value={notify.digest} onChange={(v) => setNotify({ ...notify, digest: v })} />
              <ToggleRow label="Critical alerts only" desc="Mute warning + watchlist signals" value={notify.criticalOnly} onChange={(v) => setNotify({ ...notify, criticalOnly: v })} />
              <ToggleRow label="Send to dispatcher" desc="Auto-route to dispatch queue" value={notify.toDispatcher} onChange={(v) => setNotify({ ...notify, toDispatcher: v })} />
              <ToggleRow label="Send to assigned technician" desc="Deliver alerts to tech mobile app" value={notify.toTech} onChange={(v) => setNotify({ ...notify, toTech: v })} />
              <ToggleRow label="Notify customer automatically" desc="Send comfort-impacting alerts to the customer" value={notify.toCustomer} onChange={(v) => setNotify({ ...notify, toCustomer: v })} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="integration" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm max-w-3xl">
            <div className="text-base font-semibold text-slate-900">Honeywell Device Integration</div>
            <div className="mt-1 text-sm text-slate-500">Placeholder for Honeywell Connected Home API credentials.</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="API Key" placeholder="hw_live_••••••••" mono />
              <Field label="Partner ID" placeholder="jb-notify-partner-01" mono />
              <Field label="Webhook URL" defaultValue="https://api.jbnotify.com/v1/honeywell/webhook" mono className="md:col-span-2" />
            </div>
            <div className="mt-5 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
              Integration status: <span className="font-semibold">Connected</span> · last sync 2 minutes ago
            </div>
            <Button className="mt-5" variant="outline">Re-sync Devices</Button>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm max-w-3xl">
            <div className="text-base font-semibold text-slate-900">Customer Communication Templates</div>
            <div className="mt-4 space-y-4">
              <TemplateRow title="Service visit scheduled" body="Hi {{customer_name}}, your JB Notify technician {{tech_name}} is scheduled for {{date}} at {{time}}." />
              <TemplateRow title="Alert notification" body="We detected {{alert_type}} on your {{device_model}}. Our diagnostic team has been notified." />
              <TemplateRow title="Maintenance reminder" body="It’s been {{months}} months since your last visit. Reply YES to book a tune-up." />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="plans" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm max-w-3xl">
            <div className="text-base font-semibold text-slate-900">Maintenance Plan Settings</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Auto-renewal window" defaultValue="30 days before expiry" />
              <Field label="Grace period" defaultValue="14 days" />
              <Field label="Default plan on signup" defaultValue="Preventive Maintenance" />
              <Field label="Cancellation policy" defaultValue="30 day notice" />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="dispatch" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm max-w-3xl">
            <div className="text-base font-semibold text-slate-900">Technician Dispatch Rules</div>
            <div className="mt-4 space-y-3">
              <ToggleRow label="Auto-assign by service area" desc="Route new tickets to closest available technician" value={true} />
              <ToggleRow label="Respect preferred technician" desc="Honor customer-preferred tech when available" value={true} />
              <ToggleRow label="Critical alerts bypass queue" desc="Emergency alerts skip the normal scheduling queue" value={true} />
              <ToggleRow label="Auto-reschedule on decline" desc="Automatically try next-best tech if a job is declined" value={false} />
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, defaultValue, placeholder, mono, className }) {
  return (
    <div className={`space-y-1.5 ${className || ''}`}>
      <Label className="text-xs font-medium text-slate-700">{label}</Label>
      <Input defaultValue={defaultValue} placeholder={placeholder} className={mono ? 'font-mono text-sm' : ''} />
    </div>
  );
}

function SliderField({ label, suffix, value, onValueChange, min, max, step = 1 }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-medium text-slate-800">{label}</Label>
        <span className="font-mono text-sm font-semibold text-slate-900 bg-slate-100 px-2 py-0.5 rounded-md">
          {value[0]} <span className="text-slate-500 font-normal text-xs">{suffix}</span>
        </span>
      </div>
      <Slider value={value} onValueChange={onValueChange} min={min} max={max} step={step} />
    </div>
  );
}

function ToggleRow({ label, desc, value, onChange }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-md border border-slate-200">
      <div>
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
      </div>
      <Switch checked={value} onCheckedChange={onChange} />
    </div>
  );
}

function TemplateRow({ title, body }) {
  return (
    <div className="p-3 rounded-md border border-slate-200">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <Textarea defaultValue={body} className="mt-2 font-mono text-xs" rows={2} />
    </div>
  );
}
